import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  LoadingState,
  apiRequest,
  editIcon,
  downloadIcon,
  getBootstrapData,
  TableRowActions,
} from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { TableRowAction } from '@tpct/admin-ui';
import { vsmeMissionSetupPath } from '@tpct/mission-core/utils/vsmePortalRoutes';
import type {
  CompanyPreparationMission,
  CompanyPreparationMissionsResponse,
  CompanyReportsResponse,
  CompanyReport,
} from '../../types/company-page';
import TableBadge from '../../../../hub-shared/components/TableBadge';

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const readPortalPath = (): string => {
  const raw: unknown = getBootstrapData('AccountingHubPortal');
  const portalPath = isRecord(raw) && typeof raw.portalPath === 'string' ? raw.portalPath : '/hub';
  return portalPath.replace(/\/$/, '');
};

const readPortalBootstrapVsme = (): { restUrlVsme: string; nonce: string } => {
  const raw: unknown = getBootstrapData('AccountingHubPortal');
  if (!isRecord(raw) || typeof raw.nonce !== 'string') {
    return { restUrlVsme: '', nonce: '' };
  }
  const restUrlVsme = typeof raw.restUrlVsme === 'string' ? raw.restUrlVsme : '';
  return { restUrlVsme, nonce: raw.nonce };
};

const messageFromJsonBody = (raw: unknown): string | undefined => {
  if (!isRecord(raw)) {
    return undefined;
  }
  const msg = raw.message;
  return typeof msg === 'string' ? msg : undefined;
};

type ReportsSectionProps = {
  companyId: number;
  bootstrapKey: string;
};

const buildFormUrl = (reportId: number, companyId: number, missionId: number | null): string => {
  const params = new URLSearchParams({
    report_id: String(reportId),
    company_id: String(companyId),
  });
  if (missionId !== null) {
    params.set('mission_id', String(missionId));
  }
  return `${window.location.origin}/vsme-form/?${params.toString()}`;
};

const REPORT_STATUS_LABELS: Record<string, string> = {
  draft: __('En cours'),
  generating: __('Génération…'),
  generated: __('Généré'),
  archived: __('Archivé'),
};

type ReportRowProps = {
  report: CompanyReport;
  companyId: number;
};

const formatCreationDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const ReportRow = ({ report, companyId }: ReportRowProps) => {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const editUrl = buildFormUrl(report.id, companyId, report.mission_id ?? null);
  const hasDownload = Boolean(report.download_url);
  const canOpenForm = report.mission_id !== null;

  const getApiConfig = () => {
    const { restUrlVsme, nonce } = readPortalBootstrapVsme();
    const base = restUrlVsme.endsWith('/') ? restUrlVsme : `${restUrlVsme}/`;
    return { base, nonce };
  };

  const downloadBlob = async (endpoint: string, filename: string) => {
    const { base, nonce } = getApiConfig();
    const response = await fetch(`${base}reports/${report.id}/${endpoint}`, {
      method: 'GET',
      headers: { 'X-WP-Nonce': nonce },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      const errorData: unknown = await response.json().catch(() => ({}));
      throw new Error(messageFromJsonBody(errorData) ?? __('Échec du téléchargement'));
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReportZip = async () => {
    setActionLoading('download-report-zip');
    setActionError(null);
    try {
      await downloadBlob(
        'download-report-zip',
        `rapport-${report.company_name || report.id}-${new Date().toISOString().slice(0, 10)}.zip`,
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : __('Échec du téléchargement'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadWorkFolderZip = async () => {
    setActionLoading('download-work-folder-zip');
    setActionError(null);
    try {
      await downloadBlob(
        'download-work-folder-zip',
        `dossier-travail-${report.company_name || report.id}-${new Date().toISOString().slice(0, 10)}.zip`,
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : __('Échec du téléchargement du dossier de travail'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = () => {
    if (!canOpenForm) {
      setActionError(__('Ce rapport n’est pas lié à une mission. Liaison ou migration requise.'));
      return;
    }
    window.open(editUrl, '_blank', 'noreferrer');
  };

  const actions: TableRowAction[] = [];

  if (hasDownload) {
    actions.push({
      key: 'download-report-zip',
      label: __('Rapport VS'),
      icon: downloadIcon,
      loading: actionLoading === 'download-report-zip',
      onClick: handleDownloadReportZip,
    });
    actions.push({
      key: 'download-work-folder-zip',
      label: __('Dossier de travail'),
      icon: downloadIcon,
      loading: actionLoading === 'download-work-folder-zip',
      onClick: handleDownloadWorkFolderZip,
    });
  } else {
    actions.push({
      key: 'edit',
      label: __('Modifier'),
      icon: editIcon,
      onClick: handleEdit,
    });
  }

  return (
    <>
      <tr className="company-reports-table__row">
        <td className="company-reports-table__title">
          {report.title || __('Rapport')}
        </td>
        <td className="company-reports-table__date">{formatCreationDate(report.created_at)}</td>
        <td className="company-reports-table__status">
          <TableBadge variant={`status-${report.lifecycle_state}`}>
            {REPORT_STATUS_LABELS[report.lifecycle_state] ?? report.lifecycle_state}
          </TableBadge>
        </td>
        <td className="company-reports-table__actions">
          <TableRowActions
            actions={actions}
            globalDisabled={actionLoading !== null}
            overflowLabel={__('Plus d’actions')}
          />
        </td>
      </tr>
      {actionError && (
        <tr className="company-reports-table__error-row">
          <td colSpan={4} className="company-reports-table__error">
            {actionError}
          </td>
        </tr>
      )}
    </>
  );
};

/** A mission left unfinished in its setup wizard: no report yet, so its one action resumes the wizard. */
const PreparationMissionRow = ({ mission }: { mission: CompanyPreparationMission }) => (
  <tr className="company-reports-table__row">
    <td className="company-reports-table__title">{mission.title || __('Mission VS')}</td>
    <td className="company-reports-table__date">
      {mission.created_at !== null ? formatCreationDate(mission.created_at) : '—'}
    </td>
    <td className="company-reports-table__status">
      <TableBadge variant="status-generating">{__('Préparation')}</TableBadge>
    </td>
    <td className="company-reports-table__actions">
      <TableRowActions
        actions={[{
          key: 'resume-setup',
          label: __('Reprendre'),
          icon: editIcon,
          onClick: () => window.location.assign(vsmeMissionSetupPath(mission.id, readPortalPath())),
        }]}
        overflowLabel={__('Plus d’actions')}
      />
    </td>
  </tr>
);

const ReportsSection = ({ companyId, bootstrapKey }: ReportsSectionProps) => {
  const { data, isLoading, isError } = useQuery<CompanyReportsResponse>({
    queryKey: ['company-reports', companyId],
    queryFn: () =>
      apiRequest<CompanyReportsResponse>(
        `company/${companyId}/reports`,
        {},
        bootstrapKey,
      ),
    enabled: companyId > 0,
  });

  // Missions left in preparation have no report; a failed read only leaves them out of the history.
  const { data: preparationData } = useQuery<CompanyPreparationMissionsResponse>({
    queryKey: ['company-missions-preparation', companyId],
    queryFn: () =>
      apiRequest<CompanyPreparationMissionsResponse>(
        `company/${companyId}/missions?status=preparation`,
        {},
        bootstrapKey,
      ),
    enabled: companyId > 0,
    retry: false,
  });
  const preparationMissions = preparationData?.items ?? [];

  if (isLoading) {
    return (
      <LoadingState
        columns={[__('Rapport'), __('Date'), __('Statut'), __('Action')]}
        className="company-reports-loading"
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        title={__('Erreur de chargement')}
        description={__('Impossible de charger les rapports.')}
      />
    );
  }

  const reports = data?.items ?? [];
  if (reports.length === 0 && preparationMissions.length === 0) {
    return (
      <EmptyState
        title={__('Aucune mission')}
        description={__("Aucune mission VS (vsme) n'a encore été lancée pour cette entreprise.")}
      />
    );
  }

  return (
    <div className="container-style company-reports-table-container">
      <table className="thn-table company-reports-table">
        <thead>
          <tr>
            <th className="company-reports-table__title">{__('Client')}</th>
            <th className="company-reports-table__date">{__('Date')}</th>
            <th className="company-reports-table__status">{__('Statut')}</th>
            <th className="company-reports-table__actions"></th>
          </tr>
        </thead>
        <tbody>
          {preparationMissions.map((mission) => (
            <PreparationMissionRow key={`preparation-${mission.id}`} mission={mission} />
          ))}
          {reports.map((report) => (
            <ReportRow key={report.id} report={report} companyId={companyId} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ReportsSection;
